import { KestrelGatekeeper } from 'types'

import { ToolInfo } from './types'

export const toolDistTopStr = (group: number, toolsBefore: number): string => {
  // margin top + gaps between groups + height of each tool - half the height
  return (
    'calc(3.6rem + ' +
    group * 8 +
    'px + ' +
    44 * toolsBefore +
    'px + 0.875rem - 1.25rem)'
  )
}

const countPrimaryModes = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean
): number => {
  return (
    1 + // Point Annotate
    (gatekeeper.feature.drawInspectionAreas ? 1 : 0) +
    1 + // Polygon Annotate
    1 + // SegmentationModes
    1 + // MeasurementModes
    1 + // CreateCameraViewButton
    (gatekeeper.feature.toggleInspectionGridButton ? 1 : 0) +
    (hasPrimitiveBuilderPermission ? 1 : 0)
  )
}

const countPrimaryModesBefore = (
  gatekeeper: KestrelGatekeeper,
  targetTool: 'point-annotate' | 'polygon-annotate' | 'segmentation-annotate'
): number => {
  const nav = 1 // NavigationModes
  if (targetTool === 'point-annotate') return nav
  if (targetTool === 'polygon-annotate')
    return nav + 1 + (gatekeeper.feature.drawInspectionAreas ? 1 : 0)
  return (
    nav +
    1 + // Point Annotate
    (gatekeeper.feature.drawInspectionAreas ? 1 : 0) +
    1 // Polygon Annotate
  )
}

const countBeforeViewToggles = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean
): number => {
  return 1 + countPrimaryModes(gatekeeper, hasPrimitiveBuilderPermission)
}

const countBeforeMiscTools = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean,
  photosEnabled: boolean
): number => {
  return (
    countBeforeViewToggles(gatekeeper, hasPrimitiveBuilderPermission) +
    (photosEnabled ? 1 : 0) +
    1
  )
}

const countBeforeSettings = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean,
  photosEnabled: boolean
): number => {
  return (
    countBeforeMiscTools(
      gatekeeper,
      hasPrimitiveBuilderPermission,
      photosEnabled
    ) +
    (gatekeeper.feature.exportControls ? 1 : 0) +
    1 // DeleteVMButton
  )
}

export const getAnnotationTools = (
  gatekeeper: KestrelGatekeeper
): ToolInfo[] => {
  return [
    {
      id: 'point-annotate',
      top: toolDistTopStr(
        1,
        countPrimaryModesBefore(gatekeeper, 'point-annotate')
      ),
      title: 'Point Annotate',
      description:
        'In this mode, right click on the model to create point annotations. Perfect for marking specific locations or defects.',
    },
    {
      id: 'polygon-annotate',
      top: toolDistTopStr(
        1,
        countPrimaryModesBefore(gatekeeper, 'polygon-annotate')
      ),
      title: 'Polygon Annotation',
      description:
        'Right click on the model to create the edges of a polygon annotation. Close the polygon by connecting to the starting point. Click and drag the corners of the polygon to edit.',
    },
    {
      id: 'segmentation-annotate',
      top: toolDistTopStr(
        1,
        countPrimaryModesBefore(gatekeeper, 'segmentation-annotate')
      ),
      title: 'Segmentation Annotation',
      description:
        'Right click on the model on an object you want to be selected. Kestrel will use AI to add a bounding box around that object.',
    },
  ]
}

export const getSecondToolset = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean,
  photosEnabled: boolean
): ToolInfo[] => {
  const beforeViewToggles = countBeforeViewToggles(
    gatekeeper,
    hasPrimitiveBuilderPermission
  )
  // submenu-indicator is for ViewControlsSubmenu (first tool in ViewToggles)
  const submenuIndicatorCount = beforeViewToggles
  // photoreel is after ViewControlsSubmenu
  const photoreelCount = beforeViewToggles + 1

  const tools: ToolInfo[] = [
    {
      id: 'submenu-indicator',
      top: toolDistTopStr(2, submenuIndicatorCount),
      title: 'Submenus',
      description:
        'Many tools have submenus that appear when you hover over them. These provide additional options and variations of the main tool.',
    },
  ]

  // Only include photoreel tool if photos are enabled
  if (photosEnabled) {
    tools.push({
      id: 'photoreel',
      top: toolDistTopStr(2, photoreelCount),
      title: 'Photo Reel',
      description:
        'The photo reel tool will let you see all the original photos used to make the model.',
    })
  }

  return tools
}

export const getSettingsTools = (
  gatekeeper: KestrelGatekeeper,
  hasPrimitiveBuilderPermission: boolean,
  photosEnabled: boolean
): ToolInfo[] => {
  return [
    {
      id: 'settings',
      top: toolDistTopStr(
        3,
        countBeforeSettings(
          gatekeeper,
          hasPrimitiveBuilderPermission,
          photosEnabled
        )
      ),
      title: 'Settings',
      description: 'Access Kestrel settings here.',
    },
  ]
}
